import {DotYouClient} from "@homebase-id/js-lib/core";
import {makeAutoObservable, runInAction} from "mobx";
import moment from "moment/moment";
import {
    HomebaseStorage,
    SaveFileConfig,
    SaveOptions
} from "../core/storage/HomebaseStorageTypes";
import {GuideFlowConfig} from "../../system/GuideFlowConfig";
import {SimplifiedStorageProvider} from "../core/storage/SimplifiedStorageProvider";
import {Contact} from "../contact/ContactProvider";
import axios, {AxiosInstance} from "axios";
import {
    BulkSyncResponse,
    BulkSyncResult, BulkSyncSummary,
    FluentCRMPaginatedResponse,
    FluentCRMSubscriber,
    FluentCRMTag
} from "./FluentCRMTypes";
import {chunkArray, generateSlug, sleep, unixToDateString} from "../../system/utils";

const fileConfig: SaveFileConfig = {
    dataType: 8394,
    fileType: 78929,
    targetDrive: GuideFlowConfig.DataDrive
}

export interface CreateFluentCRMTag {
    title: string;
    slug?: string;        // optional → will be auto-generated if missing
    description?: string; // optional
}


const configUid = "c02a59b7-b7b5-4d9b-a4b1-09772be892cf";


export interface FluentCRMContact {
    id: number;
    user_id?: number;
    hash?: string;
    email: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    status?: string;
    contact_type?: string;
    phone?: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
    date_of_birth?: string;
    photo?: string;
    custom_values?: Record<string, any>;
    created_at?: string;
    updated_at?: string;
}

export interface FluentCrmConfig extends HomebaseStorage {
    fileId?: string,
    id: string;
    websiteUrl: string,
    apiKey: string
}

export class FluentCrmProvider {
    private static instance: FluentCrmProvider
    private readonly provider: SimplifiedStorageProvider<FluentCrmConfig>;
    private cachedConfig: FluentCrmConfig | null = null;

    public StateChanged: number = 0;

    constructor(config: SaveFileConfig, dotYouClient: DotYouClient) {
        this.provider = new SimplifiedStorageProvider<FluentCrmConfig>(config, dotYouClient);
        makeAutoObservable(this);
        this.synchronize = this.synchronize.bind(this);
    }

    static getInstance(dotYouClient: DotYouClient) {
        if (FluentCrmProvider.instance == null) {
            FluentCrmProvider.instance = new FluentCrmProvider(fileConfig, dotYouClient);
        }
        return FluentCrmProvider.instance;
    }

    public async exportGraph() {
        return {
            FluentCrmConfig: await this.provider.exportGraph("fluent-crm-config"),
        };
    }

    public async syncFileType(): Promise<number> {
        const count = await this.provider.syncFileType(moment.utc().unix());
        if (count > 0) {
            this.notifyStateChange();
        }
        return count;
    }

    public async saveConfig(websiteUrl: string, apiKey: string): Promise<string> {

        const options: SaveOptions =
            {
                groupId: undefined,
                userDate: undefined,
                onVersionConflict: () => {
                    //TODO: how to handle this?
                }
            };

        const existingConfig = await this.getConfig();
        const config: FluentCrmConfig = {
            ...(existingConfig ?? {id: configUid}),
            id: configUid,
            websiteUrl,
            apiKey,
        };

        const result = await this.provider.saveHeaderFile(config, undefined, options);

        if (result) {
            this.cachedConfig = config;
            this.notifyStateChange();
        }

        return "";
    }

    public async getConfig(): Promise<FluentCrmConfig | null> {
        if (this.cachedConfig) {
            return this.cachedConfig;
        }

        const config = await this.provider.getByUniqueId(configUid);

        // @ts-ignore
        this.cachedConfig = config ?? null;
        return this.cachedConfig;
    }

    public async synchronize(asOfTime: number) {
        const count = await this.provider.syncFileType(asOfTime);
        if (count > 0) {
            this.notifyStateChange();
        }
    }

    public async addTagsToContact(subscriberId: string, tagIds: string[]): Promise<void> {
        const fluentCrmApi = await this.createFluentCrmApi();

        try {
            await fluentCrmApi.post(`/subscribers/${subscriberId}/attach-tags`, {
                tags: tagIds,
            });
        } catch (error: any) {
            if (error.response) {
                const status = error.response.status;
                const message =
                    error.response.data?.message ||
                    error.response.statusText ||
                    "Unknown error attaching tags";

                throw new Error(`FluentCRM API error (${status}): ${message}`);
            }
            throw new Error(`FluentCRM connection error: ${error.message}`);
        }
    }

    public async removeTagsFromContact(subscriberId: string, tagIds: string[]): Promise<void> {
        const fluentCrmApi = await this.createFluentCrmApi();

        try {
            await fluentCrmApi.post(`/subscribers/${subscriberId}/detach-tags`, {
                tags: tagIds,
            });
        } catch (error: any) {
            if (error.response) {
                const status = error.response.status;
                const message =
                    error.response.data?.message ||
                    error.response.statusText ||
                    "Unknown error detaching tags";

                throw new Error(`FluentCRM API error (${status}): ${message}`);
            }
            throw new Error(`FluentCRM connection error: ${error.message}`);
        }
    }

    public async getOrCreateTag(title: string): Promise<FluentCRMTag> {
        const fluentCrmApi = await this.createFluentCrmApi();

        // Normalize slug
        const slug = title.toLowerCase().replace(/\s+/g, "-");

        // 1. Get existing tags
        const tagsRes = await fluentCrmApi.get<{ tags: FluentCRMPaginatedResponse<FluentCRMTag> }>("/tags");
        const existing = tagsRes.data.tags.data.find(
            (t) => t.slug === slug || t.title.toLowerCase() === title.toLowerCase()
        );

        if (existing) {
            return existing;
        }

        // 2. Create new tag
        const createRes = await fluentCrmApi.post<{ tag: FluentCRMTag }>("/tags", {
            title,
            slug,
        });

        return createRes.data.tag;
    }

    public async getContacts(
        perPage: number = 10,
        page: number = 1
    ): Promise<FluentCRMPaginatedResponse<FluentCRMSubscriber>> {
        const fluentCrmApi = await this.createFluentCrmApi();

        try {
            const response = await fluentCrmApi.get<{ subscribers: FluentCRMPaginatedResponse<FluentCRMSubscriber> }>(
                "/subscribers",
                {
                    params: {
                        per_page: perPage,
                        page: page,
                    },
                }
            );

            return response.data.subscribers;
        } catch (error: any) {
            if (error.response) {
                const status = error.response.status;
                const message =
                    error.response.data?.message ||
                    error.response.statusText ||
                    "Unknown error from FluentCRM API";

                throw new Error(`FluentCRM API error (${status}): ${message}`);
            }

            throw new Error(`FluentCRM connection error: ${error.message}`);
        }

    }

    public async syncContact(
        contact: Contact
    ): Promise<{ action: "created" | "updated"; record: FluentCRMSubscriber }> {
        const fluentCrmApi = await this.createFluentCrmApi();

        // 1. Search for existing subscriber by email
        const searchRes = await fluentCrmApi.get<{
            contacts: Record<string, FluentCRMSubscriber>;
        }>("/subscribers/search-contacts", {params: {search: contact.email}});

        const contactsObj = searchRes.data.contacts || {};
        try {
            const existing = Object.values(contactsObj).find(
                (c) => c.email.toLowerCase() === contact.email.toLowerCase()
            );

            // 2. Build payload
            const payload: Partial<FluentCRMSubscriber> = {
                email: contact.email,
                first_name: contact.firstname,
                last_name: contact.surname,
                phone: contact.phone,
                address_line_1: contact.primaryAddress?.addressLine1,
                address_line_2: contact.primaryAddress?.addressLine2,
                city: contact.primaryAddress?.city,
                state: contact.primaryAddress?.stateProvinceRegion,
                country: contact.primaryAddress?.country,
                postal_code: contact.primaryAddress?.postalCode,
                date_of_birth: contact.dateOfBirth
                    ? unixToDateString(contact.dateOfBirth)
                    : undefined,
            };

            // 3. Update or Create
            if (existing) {
                const updateRes = await fluentCrmApi.put<{ subscriber: FluentCRMSubscriber }>(
                    `/subscribers/${existing.id}`,
                    payload
                );
                return {action: "updated", record: updateRes.data.subscriber};
            } else {
                const createRes = await fluentCrmApi.post<{ subscriber: FluentCRMSubscriber }>(
                    "/subscribers",
                    payload
                );
                return {action: "created", record: createRes.data.subscriber};
            }
        } catch (err) {
            console.log("OMG HERE I AM", err);
        }

    }

    public async syncContactWithTags(
        contact: Contact,
        tagNames: string[]
    ): Promise<{ action: "created" | "updated"; record: FluentCRMSubscriber }> {
        // 1. Upsert the contact
        const {action, record} = await this.syncContact(contact);

        if (tagNames.length > 0) {
            const fluentCrmApi = await this.createFluentCrmApi();

            // 2. Fetch existing tags
            const tagsRes = await fluentCrmApi.get<{ tags: FluentCRMPaginatedResponse<FluentCRMTag> }>("/tags");
            const existingTags = tagsRes.data;

            const tagIds: string[] = [];

            for (const name of tagNames) {
                const slug = name.toLowerCase().replace(/\s+/g, "-");

                // find existing tag
                const match = existingTags.tags.data.find(
                    (t) => t.slug === slug || t.title.toLowerCase() === name.toLowerCase()
                );

                if (match) {
                    tagIds.push(match.id);
                } else {
                    // create new tag
                    const createRes = await fluentCrmApi.post<{ tag: FluentCRMTag }>("/tags", {
                        title: name,
                        slug,
                    });
                    tagIds.push(createRes.data.tag.id);
                }
            }

            // 3. Attach tags (skip if none resolved)
            if (tagIds.length > 0) {
                await fluentCrmApi.post(`/subscribers/${record.id}/attach-tags`, {
                    tags: tagIds,
                });
            }
        }

        return {action, record};
    }

    public async bulkSyncContacts(contacts: Contact[]): Promise<BulkSyncResponse> {
        const fluentCrmApi = await this.createFluentCrmApi();
        const result: BulkSyncResult = {created: [], updated: [], failed: []};

        try {
            const payload = {
                contacts: contacts
                    .filter((c): c is Contact => c != null && !!c.email) // drop undefined / no email
                    .map(c => ({
                        email: c.email,
                        first_name: c.firstname,
                        last_name: c.surname,
                        phone: c.phone,
                        address_line_1: c.primaryAddress?.addressLine1,
                        address_line_2: c.primaryAddress?.addressLine2,
                        city: c.primaryAddress?.city,
                        state: c.primaryAddress?.stateProvinceRegion,
                        country: c.primaryAddress?.country,
                        postal_code: c.primaryAddress?.postalCode,
                        date_of_birth: c.dateOfBirth
                            ? unixToDateString(c.dateOfBirth)
                            : undefined,
                    }))
            };

            const res = await fluentCrmApi.post<{ results: any[] }>(
                "/subscribers/bulk-add-update",
                payload
            );

            for (const r of res.data.results) {
                if (r.error) {
                    const failedContact = contacts.find(c => c.email === r.email);
                    result.failed.push({
                        contact: failedContact ?? {
                            id: "",
                            firstname: "",
                            surname: "",
                            email: r.email,
                            phone: "",
                            mmsStudentNumber: "",
                            dateOfBirth: 0
                        },
                        error: r.error
                    });
                } else if (r.action === "created") {
                    result.created.push(r.subscriber as FluentCRMSubscriber);
                } else if (r.action === "updated") {
                    result.updated.push(r.subscriber as FluentCRMSubscriber);
                }
            }
        } catch (error: any) {
            // fallback: per-contact sync if bulk fails
            for (const c of contacts) {
                try {
                    const {action, record} = await this.syncContact(c);
                    if (action === "created") result.created.push(record);
                    else result.updated.push(record);
                } catch (err: any) {
                    result.failed.push({contact: c, error: err.message});
                }
            }
        }

        const summary: BulkSyncSummary = {
            createdCount: result.created.length,
            updatedCount: result.updated.length,
            failedCount: result.failed.length,
            createdEmails: result.created.map(c => c.email),
            updatedEmails: result.updated.map(c => c.email),
            failed: result.failed.map(f => ({
                email: f.contact.email,
                error: f.error
            }))
        };

        return {result, summary};
    }

    public async bulkCreateTags(
        tags: CreateFluentCRMTag[],
        onStatusChange: (status: string) => void,
        chunkSize = 2,
        delayMs = 1000
    ): Promise<{
        created: FluentCRMTag[];
        existing: FluentCRMTag[];
        failed: { title: string; error: string }[];
    }> {
        
        console.info(`Creating tags`, tags);
        const fluentCrmApi = await this.createFluentCrmApi();
        const results = {
            created: [] as FluentCRMTag[],
            existing: [] as FluentCRMTag[],
            failed: [] as { title: string; error: string }[],
        };

        try {
            // Get all existing tags once
            const tagsRes = await fluentCrmApi.get<{ tags: { data: FluentCRMTag[] } }>("/tags");
            const existingTags = tagsRes.data.tags.data;
            
            console.info(`Existing tags`, existingTags);

            // Process in chunks
            const chunks = chunkArray(tags, chunkSize);

            for (const chunk of chunks) {
                for (const {title, slug, description} of chunk) {
                    const finalSlug = slug ?? generateSlug(title);

                    onStatusChange(`Processing tag: ${title}`);
                    const match = existingTags.find(
                        (t) => t.slug === finalSlug
                    );

                    if (match) {
                        results.existing.push(match);
                        onStatusChange(`Tag already exists: ${finalSlug}`);
                        continue;
                    }

                    try {
                        onStatusChange(`Creating tag: ${finalSlug}`);
                        const res = await fluentCrmApi.post<{ tag: FluentCRMTag }>("/tags", {
                            title,
                            slug: finalSlug,
                            description: description ?? "",
                        });
                        results.created.push(res.data.tag);
                        // existingTags.push(res.data.tag); // add to in-memory list
                        onStatusChange(`Successfully created tag: ${finalSlug}`);
                        
                    } catch (err: any) {
                        results.failed.push({title, error: err.message ?? "Unknown error"});
                        console.info(`Creating tag error: ${err.message}`);
                    }
                }

                // Throttle between chunks
                if (delayMs > 0) {
                    await sleep(delayMs);
                }
            }
        } catch (err: any) {
            for (const {title} of tags) {
                results.failed.push({title, error: err.message ?? "Unknown error"});
            }
        }

        return results;
    }


    private notifyStateChange() {
        runInAction(() => {
            this.StateChanged++;
        });
    }

    private async createFluentCrmApi(): Promise<AxiosInstance> {
        const cfg = await this.getConfig();
        const websiteUrl = cfg.websiteUrl;
        const apiKey = cfg.apiKey;

        const token = btoa(apiKey);

        return axios.create({
            baseURL: `${websiteUrl}/wp-json/fluent-crm/v2`,
            headers: {
                Authorization: `Basic ${token}`,
                "Content-Type": "application/json",
            },
        });
    }
}
